import {
  // Checkbox,
  FormControl,
  InputBase,
  InputLabel as MuiInputLabel,
  ListItemText,
  MenuItem,
  Select as MuiSelect,
  styled,
  useTheme,
  Typography,
  ListSubheader,
  InputAdornment,
  Box,
  SelectChangeEvent,
} from '@mui/material';
import {useMediaQuery} from '@mui/material';
import {useEffect, useMemo, useState, useRef} from 'react';
import type {SelectProps, OptionType} from '..';
import {
  ChevronDown,
  CheckIcon,
  UnCheckIcon,
  SearchIcon,
  CheckedHirist,
} from '@jobseeker/lotus/icons';
import {Image} from '@jobseeker/lotus/components/Image';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {Search} from '@jobseeker/lotus/icons';
import TextField from '@jobseeker/lotus/elements/Input';

const ImageV2 = styled(Image)(({theme}) => ({
  userSelect: 'none',
  padding: '2px',
  marginRight: theme.spacing(1.7),
  marginLeft: theme.spacing(1.7),
  marginTop: theme.spacing(0.4),
  transition: '0.3s ease-in-out',
  [theme.breakpoints.between('md', 'lg')]: {
    // marginRight: 1.5,
  },
}));

const containsText = (text: string, searchText: string) =>
  text?.toLowerCase().indexOf(searchText?.toLowerCase()) > -1;

const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

export const RoundedInput: any = styled(InputBase)(({theme, placeholder}) => ({
  'label + &': {
    paddingRight: {lg: 5},
  },
  '& .MuiInputBase-input': {
    borderRadius: '100px',
    position: 'relative',
    backgroundColor: isAppIimjobs ? '#fff' : '#fff',
    color:
      placeholder || !(process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs')
        ? '#7e7e7e'
        : '#333',
    fontSize: process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs' ? 14 : 17,
    fontWeight: '500',
    lineHeight: '21px',
    padding: theme.spacing(1, 1.5),
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    '&:focus': {
      borderRadius: '100px',
      borderColor: '#33333390',
    },
    '&:hover': {
      borderRadius: '100px',
      borderColor: '#33333390',
    },
  },
}));

const InputLabel = styled(MuiInputLabel)(({theme}) => ({
  color: '#000',
  fontSize: theme.typography.pxToRem(14),
  background: '#fff',
  lineHeight: theme.typography.pxToRem(24),
  fontWeight: 500,
  paddingRight: 2,
  width: '60%',
  zIndex: 4,
}));

export const SelectIcon = ({className, open, sx}: any) => {
  const theme = useTheme();
  return (
    <ImageV2
      className={className}
      src={ChevronDown}
      alt={'Drop Down Arrow'}
      width={12}
      height={12}
      style={{
        zIndex: 0,
        transition: 'none',
        ...sx,
      }}
      objectFit="cover"
    />
  );
};
export const Checkbox = ({checked}: any) => {
  return (
    <Image
      src={checked ? (isAppIimjobs ? CheckIcon : CheckedHirist) : UnCheckIcon}
      alt="checkbox"
      width={isAppIimjobs ? 12 : 16}
      height={isAppIimjobs ? 12 : 16}
    />
  );
};

const Select: any = styled(MuiSelect)(({theme, variant}: any): any => ({
  '.MuiPaper-elevation': {
    boxShadow: 'none',
  },
  '.MuiInputBase-input': {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
}));

export default (props: SelectProps) => {
  const SelectValue: any = props?.value ?? [];
  const ref = useRef();

  const [searchText, setSearchText] = useState('');
  const displayedOptions = useMemo(() => {
    return props.multiple
      ? props.options.filter(option =>
          containsText(option.label.toString(), searchText)
        )
      : props.options;
  }, [searchText]);

  const closeMenu = () => {
    if (props.setMenuOpen) props.setMenuOpen(false);
    setTimeout(() => {
      setSearchText('');
    }, 100);
  };
  const handleChange = (event: any) => {
    let {
      target: {value},
    } = event;
    const newValue = handleValue(value);
    props.onChange && props.onChange(event, newValue);
  };
  const [viewportHeight, setViewportHeight] = useState<number>(0);

  useEffect(() => {
    const updateHeight = () => setViewportHeight(window.innerHeight);
    updateHeight();

    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);
  const renderValue = (selected: any) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.between('xxs', 'sm'));
    if (!selected || (typeof selected === 'object' && !selected.length))
      return props.label;

    if (props.multiple) {
      return `${selected.length ?? SelectValue.length} Selected`;
    } else {
      const selectedObjects = props.options.filter(
        option => selected === option?.value
      );
      // return selectedObjects.map(option => option.label).join(', ');
      const selectedLabel = selectedObjects
        .map(option => option.label)
        .join(', ');
      if (props.truncatelength && isMobile) {
        const truncatedText =
          selectedLabel.length > props.truncatelength
            ? `${selectedLabel.substring(0, props.truncatelength)}...`
            : selectedLabel;

        return (
          <Typography
            display={'flex'}
            justifyContent={'flex-start'}
            alignItems={'center'}
            gap={1}
            sx={{
              color: '#333',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '18px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
            }}
          >
            {truncatedText}
          </Typography>
        );
      }
      return selectedLabel;
    }
  };

  const handleValue = (optionValue: string | number) => {
    const option: OptionType | undefined = props.options.find(
      option => option?.value === optionValue
    );
    if (!option) {
      return false;
    }
    const value = option?.value ?? option.id ?? option;
    const isAlreadySelected = SelectValue?.indexOf(value) > -1;
    if (!isAlreadySelected) {
      if (props.multiple) {
        let finalList = [value];
        if (option.selected) {
          finalList = finalList.concat(option.selected);
        }

        return SelectValue.concat(finalList);
      } else {
        return [value];
      }
    } else {
      const filteredOptions = SelectValue.filter((_value: never) => {
        if (_value === value) {
          return false;
        }
        if (option?.selected?.length) {
          return option.selected?.indexOf(_value) === -1;
        }
        return true;
      });

      return filteredOptions;
    }
  };
  const handleOnClose = (e: any) => {
    if (!props.multiple) {
      closeMenu();
    }
    setTimeout(() => {
      setSearchText('');
    }, 100);
    return;
  };

  // MUI IS PLANNING TO MAKE SELECT A NON MODAL SELECT UNTIL THEN WE HAVE TO USE PATCH FIXES FOR SCROLL LOCK ON BODY
  // https://github.com/mui/material-ui/issues/17353#issuecomment-1703289570
  // This was asked to be removed by Design team (Arun), dated 11-12-2024.
  // useEffect(() => {
  //   const handler = () => {
  //     props.setMenuOpen && props.setMenuOpen(false);
  //   };
  //   window?.addEventListener('scroll', handler);
  //   return () => {
  //     window.removeEventListener('scroll', handler);
  //   };
  // }, []);
  const key = props.label?.toString().replace(' ', '-');
  const LabelComp = [
    VersionVariants.VERSION_TWO,
    VersionVariants.VERSION_HIRIST,
  ].includes(props.variant)
    ? InputLabel
    : MuiInputLabel;

  return (
    <FormControl
      size="small"
      sx={
        props?.maxWidth
          ? {width: '100%', maxWidth: props.maxWidth}
          : {width: '100%'}
      }
    >
      {/* <LabelComp id={`lotus-selectlabel-${key}`}>{props.label}</LabelComp> */}
      <Select
        labelId={`lotus-selectlabel-${key}`}
        id={`lotus-select-${props.name}`}
        data-name={props.name}
        // label={props.label}
        displayEmpty
        ref={ref}
        sx={props.sx ? {...props.sx} : {}}
        value={
          SelectValue.length
            ? props.multiple
              ? 9999
              : SelectValue[0]
            : SelectValue
        }
        open={props.menuOpen}
        inputProps={{
          'data-name': props.name,
          name: props.name,
          sx: {
            zIndex: 0,
          },
        }}
        onClose={handleOnClose}
        onOpen={() => props.setMenuOpen && props.setMenuOpen(props.name)}
        MenuProps={{
          onClose: closeMenu,
          disableScrollLock: true,
          // marginThreshold: null,
          // disablePortal: true,
          transitionDuration: 100,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          sx: {
            maxHeight: props.hirist
              ? {xxs: 300, md: 550}
              : viewportHeight < 790 &&
                  (props?.page === 'alumni' || props?.page === 'women_at_work')
                ? props?.page === 'alumni'
                  ? 300
                  : 250
                : 400,
            minWidth: {
              xxs: 200,
              md: isAppIimjobs ? 400 : 200,
              lg: isAppIimjobs ? 500 : 200,
            },
            // position: 'absolute',
            // minHeight: 400,
            paddingTop: 0,
            '.MuiMenu-list': {
              paddingTop: props.multiple ? 0 : 1,
              paddingBottom: props.multiple ? 0 : 1,
            },
            boxShadow: 'none !important',
            '.MuiPaper-elevation': {
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              marginTop: props.hirist ? {xxs: 1, md: 1} : 0.7,
              ...(props?.hirist ? {minHeight: '320px'} : {}),
              // left: '0 !important',
              // top: '40px !important',
            },
            zIndex: {xxs: 3, md: 1},
            cursor: 'default',
            bgcolor: {xxs: 'unset', md: 'unset'},
            backdropFilter: {xxs: 'unset', md: 'unset'},
            ul: {py: '0px'},
          },
          autoFocus: props.multiple ? false : true,
          ...props.MenuProps,
        }}
        onChange={handleChange}
        renderValue={renderValue}
        input={
          [
            VersionVariants.VERSION_TWO,
            VersionVariants.VERSION_HIRIST,
          ].includes(props.variant) ? (
            <RoundedInput
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: !props.hirist ? 14 : 17,

                  color: props.hirist ? 'rgba(0,0,0,0.4)' : '#333',
                  border:
                    props.hirist && !useMediaQuery('(max-width: 500px)')
                      ? '0px'
                      : '1px solid #7E7E7E33',

                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '30%', // Start at 25% from the top
                    height: '40%', // Cover 50% of the height
                    width: '1px', // Border thickness
                    backgroundColor: '#7E7E7E33', // Border color,
                    display:
                      props.hirist && useMediaQuery('(min-width: 500px)')
                        ? 'block'
                        : 'none',
                  },
                  borderRadius: props.hirist
                    ? {xxs: '8px !important', md: '0px !important'}
                    : '100px',
                  pt: props.hirist ? '16px !important' : '8px',
                  pb: props.hirist ? '16px !important' : '8px',

                  '&:focus': {
                    border: props.hirist
                      ? {xxs: '1px solid #7E7E7E33', md: 'none'}
                      : '',

                    borderRadius: props.hirist
                      ? {xxs: '8px !important', md: '0px !important'}
                      : '100px',
                  },
                  '&:hover': {
                    border: props.hirist
                      ? {xxs: '1px solid #7E7E7E33', md: 'none'}
                      : '',
                    borderRadius: props.hirist
                      ? {xxs: '8px !important', md: '0px !important'}
                      : '100px',
                  },
                },
              }}
              name={props.name}
              placeholder={SelectValue.length ? undefined : props.placeholder}
            />
          ) : undefined
        }
        IconComponent={
          [
            VersionVariants.VERSION_TWO,
            VersionVariants.VERSION_HIRIST,
          ].includes(props.variant)
            ? ({...iconProps}) => (
                <SelectIcon {...iconProps} open={props.menuOpen} />
              )
            : undefined
        }
        name={props.name}
      >
        {props.multiple && (
          <ListSubheader
            sx={{lineHeight: '10px', paddingTop: 1.5, paddingBottom: 1.5}}
          >
            <TextField
              // size="small"
              // Autofocus on textfield
              autoFocus
              placeholder={
                props.hirist
                  ? `Search job ${props.label} here...`
                  : `Search ${props.label}`
              }
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Image
                      src={SearchIcon}
                      alt="search"
                      width={12}
                      height={12}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },

                '& .MuiOutlinedInput-notchedOutline': {
                  border: props.hirist
                    ? '1px solid #7E7E7E33 !important'
                    : 'none', // Always visible border
                },

                '.MuiInputBase-input': {
                  border: 'unset !important',
                  fontSize: props.hirist
                    ? '14px !important'
                    : '10px !important',
                  paddingLeft: 0,
                  backgroundColor: props.hirist ? '#F2F5FA' : 'transparent',
                  paddingTop: props.hirist ? '8px !important' : '0px',
                  paddingBottom: props.hirist ? '8px !important' : '0px',
                },
                '.MuiInputBase-root': {
                  borderRadius: props.hirist ? '4px !important' : '100px',
                  backgroundColor: props.hirist ? '#F2F5FA' : 'transparent',
                  paddingTop: props.hirist ? '-8px !important' : '8px',
                  paddingBottom: props.hirist ? '-8px !important' : '8px',
                  border: props.hirist ? 'none' : '1px solid #7E7E7E33',
                },
              }}
              value={searchText}
              onChange={e => setSearchText(e.target?.value)}
              onKeyDown={e => {
                if (e.key !== 'Escape') {
                  // Prevents autoselecting item while typing (default Select behaviour)
                  e.stopPropagation();
                }
              }}
              variant={VersionVariants.VERSION_TWO}
            />
          </ListSubheader>
        )}
        {displayedOptions?.length ? (
          displayedOptions.map((option: any, i) => (
            <MenuItem
              key={`${props.name ?? i}-${i}-${option?.value}`}
              value={option?.value}
              sx={{
                fontSize: props.hirist ? 16 : 12,
                ':hover, &.Mui-selected': {
                  backgroundColor: props.hirist
                    ? props.multiple
                      ? 'transparent'
                      : '#eff3fa'
                    : props.hiristColor
                      ? 'rgba(233, 99, 12, 0.08)'
                      : !isAppIimjobs
                        ? props.multiple
                          ? 'transparent'
                          : '#eff3fa'
                        : '#91B87929',
                },
                my: props.hirist ? 0.5 : 0,
                '&.Mui-selected': {
                  '*': {
                    fontWeight: 500,
                  },
                },
              }}
            >
              <Typography
                fontSize={props.hirist ? 16 : 12}
                color={props.hirist ? '#555 !important' : '#333'}
                display={'flex'}
                alignItems={'center'}
                gap={1}
                fontWeight={SelectValue.includes(option?.value) ? 500 : 400}
              >
                {props.multiple && (
                  <Checkbox checked={SelectValue.includes(option?.value)} />
                )}
                {option.label}
              </Typography>
            </MenuItem>
          ))
        ) : (
          <MenuItem
            key={`no-results-found`}
            disabled={true}
            sx={{
              fontSize: 12,
              ':hover, &.Mui-selected': {
                backgroundColor: !isAppIimjobs ? 'transparent' : '#91B87929',
              },
              '&.Mui-selected': {
                '*': {
                  fontWeight: 500,
                },
              },
            }}
          >
            {typeof props.label == 'string'
              ? `No ${props.label.toLowerCase()} found`
              : 'No results found'}
          </MenuItem>
        )}
        {props.hirist && props.multiple ? (
          <ListSubheader
            sx={{
              position: 'sticky',
              bottom: 0,
              borderTop: '1px solid #eee', // Optional separator
              boxShadow: '0 3px 31px 0 rgba(0,0,0,0.09)',
              py: 1.5,
              paddingRight: 3.5,
            }}
          >
            <Typography
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                fontSize: 14,
                color: 'rgba(0,0,0,0.5)',
                cursor: 'pointer',
              }}
              onClick={() => {
                if (props.onChange) {
                  const event = {
                    target: {
                      value: props.multiple ? [] : '',
                      name: props.name || '', // Ensure name exists if required
                    },
                  } as SelectChangeEvent<unknown>;

                  props.onChange(event, null);
                }
              }}
            >
              Reset
            </Typography>
          </ListSubheader>
        ) : (
          <></>
        )}
      </Select>
    </FormControl>
  );
};
